// eslint-disable

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Pagination } from 'src/components/datagrid'
import { useSnackbar } from 'src/components/snackbar'
import { useAuthContext } from 'src/auth/hooks/use-auth-context'

import axiosInstance from 'src/utils/axios'

// Types for ticket validation
export type IndividualTicket = {
  ticketNumber: number
  qrCodeHash: string
  isValidated: boolean
  validatedAt?: string
  validatedBy?: {
    _id: string
    name: string
  } | null
}

export type TicketValidationStatus = {
  totalTickets: number
  validatedTickets: number
  pendingTickets: number
  isFullyValidated: boolean
  validationProgress: number
  qrCodeHash: string
}

export type TicketProduct = {
  _id: string
  number: number
  product: {
    _id: string
    name: string
  }
  name: string
  quantity: number
  price: number
  validateQrcode: {
    enabled: boolean
  }
  tickets?: IndividualTicket[]
  scans: Array<{
    scanedBy?: {
      _id: string
      name: string
    } | null
    date: string
  }>
  validationStatus: TicketValidationStatus
}

export type TicketOrder = {
  _id: string
  number: number
  createdAt: string
  event?: {
    _id: string
    name: string
  } | null
  saler?: {
    _id: string
    name: string
  } | null
  products: TicketProduct[]
  orderValidationStatus: {
    totalTickets: number
    totalValidated: number
    totalPending: number
    isFullyValidated: boolean
    validationProgress: number
  }
}

export type PendingValidationResponse = {
  data: TicketOrder[]
  total: number
  limit: number
  totalPages: number
  page: number
  extraData: {
    totalPendingTickets: number
    totalValidatedTickets: number
    totalTicketsRequiringValidation: number
    ordersWithPendingValidation: number
    fullyValidatedOrders: number
    totalOrdersWithValidation: number
    validationProgress: number
  }
}

// Types for filters
export type FilterEvent = {
  _id: string
  name: string
  startDate: string
  endDate: string
  label: string
  value: string
}

export type FilterProduct = {
  _id: string
  name: string
  price: number
  event: {
    _id: string
    name: string
  }
  category: string
  requiresValidation: boolean
  label: string
  value: string
}

export type FiltersData = {
  events: FilterEvent[]
  products: FilterProduct[]
  productsByEvent: { [eventName: string]: FilterProduct[] }
  meta: {
    totalEvents: number
    totalProducts: number
    userRole: string
    isAdmin: boolean
  }
}

async function fetchFiltersData(): Promise<FiltersData> {
  const res = await axiosInstance.get('/order/filters-data')
  return res.data
}

async function fetchPendingValidation(
  pagination: Pagination,
  filters: any,
): Promise<PendingValidationResponse> {
  let sortName: any = 'createdAt'
  let order: string = 'desc'

  if (pagination.sort) {
    const keyNames = Object.keys(pagination.sort)
    sortName = keyNames[0]
    order = pagination.sort[sortName]
  }

  const res = await axiosInstance.get('/order/pending-validation', {
    params: {
      ...pagination,
      ...filters,
      sort: sortName,
      order: order === 'desc' ? -1 : 1,
      event: filters?.event?.value || filters?.event,
      product: filters?.product?.value || filters?.product,
      status: filters?.status,
      filter: filters?.search,
    },
  })
  return res.data
}

async function validateTicket(validationData: {
  hash?: string
  orderNumber?: string
  arrayNumber?: string
}): Promise<any> {
  const res = await axiosInstance.post('/order/validate', validationData)
  return res.data
}

// Hook para buscar dados dos filtros
export function useFiltersData() {
  return useQuery(['filters-data'], fetchFiltersData, {
    staleTime: 300000, // 5 minutos
    refetchOnWindowFocus: false,
  })
}

// Hook para listar tickets pendentes de validação
export function usePendingValidation(pagination: Pagination, filters?: any) {
  return useQuery(
    ['pending-validation', pagination, filters],
    () => fetchPendingValidation(pagination, filters),
    {
      keepPreviousData: true,
      staleTime: 60000, // 1 minuto - permite refresh em mudanças de filtro
      refetchInterval: false, // Disable auto refetch
      refetchOnWindowFocus: false, // Disable refetch on window focus
      refetchIntervalInBackground: false,
      refetchOnReconnect: false, // Disable refetch on reconnect
      // Permite refetch quando filtros mudarem
    },
  )
}

// Hook para validar ticket
export function useValidateTicket() {
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()
  const { user } = useAuthContext()

  return useMutation({
    mutationFn: validateTicket,
    onSuccess: (data) => {
      // Atualizar apenas as queries em cache sem refetch
      queryClient.setQueriesData(
        ['pending-validation'],
        (oldData: PendingValidationResponse | undefined) => {
          if (!oldData || !data?.order) return oldData

          // Validar se os dados do servidor estão completos
          if (!data.order._id || !Array.isArray(data.order.products)) {
            console.warn('Dados do servidor incompletos, mantendo dados originais')
            return oldData
          }

          // Encontrar e atualizar o pedido com dados reais do servidor
          const updatedData = {
            ...oldData,
            data: oldData.data.map((order) => {
              if (order._id === data.order._id) {
                // Usar dados do servidor que agora vêm populados
                return {
                  ...order,
                  ...data.order,
                  // Recalcular status do pedido baseado nos produtos atualizados
                  orderValidationStatus: {
                    ...order.orderValidationStatus,
                    totalValidated: data.order.products.reduce(
                      (sum: number, p: any) => sum + (p.validationStatus?.validatedTickets || 0),
                      0,
                    ),
                    totalPending: data.order.products.reduce(
                      (sum: number, p: any) => sum + (p.validationStatus?.pendingTickets || 0),
                      0,
                    ),
                    isFullyValidated: data.order.products.every(
                      (p: any) => p.validationStatus?.isFullyValidated || false,
                    ),
                    validationProgress: (() => {
                      const totalTickets = data.order.products.reduce(
                        (sum: number, p: any) => sum + (p.validationStatus?.totalTickets || 0),
                        0,
                      )
                      const totalValidated = data.order.products.reduce(
                        (sum: number, p: any) => sum + (p.validationStatus?.validatedTickets || 0),
                        0,
                      )
                      return totalTickets > 0 ? (totalValidated / totalTickets) * 100 : 0
                    })(),
                  },
                }
              }
              return order
            }),
          }

          // Atualizar estatísticas globais se disponíveis
          if (updatedData.extraData) {
            try {
              const allOrders = updatedData.data
              const newTotalValidated = allOrders.reduce(
                (sum, order) => sum + (order.orderValidationStatus?.totalValidated || 0),
                0,
              )
              const newTotalPending = allOrders.reduce(
                (sum, order) => sum + (order.orderValidationStatus?.totalPending || 0),
                0,
              )

              updatedData.extraData = {
                ...updatedData.extraData,
                totalValidatedTickets: newTotalValidated,
                totalPendingTickets: newTotalPending,
                validationProgress:
                  (updatedData.extraData.totalTicketsRequiringValidation || 0) > 0
                    ? (newTotalValidated / updatedData.extraData.totalTicketsRequiringValidation) *
                      100
                    : 0,
              }
            } catch (error) {
              console.warn('Erro ao atualizar estatísticas globais:', error)
              // Manter extraData original em caso de erro
            }
          }

          return updatedData
        },
      )

      enqueueSnackbar('Ticket validado com sucesso! 🎫', {
        variant: 'success',
        autoHideDuration: 2000,
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
      })
    },
    onError: (error: any) => {
      let errorMessage = 'Erro ao validar ticket'

      if (error.response?.data?.error || error.response?.data?.erro) {
        errorMessage = error.response.data.error || error.response.data.erro
      } else if (error.response?.status === 400) {
        errorMessage = 'Dados inválidos ou ticket não encontrado'
      } else if (error.response?.status === 401) {
        errorMessage = 'Não autorizado para esta validação'
      } else if (error.response?.status === 500) {
        errorMessage = 'Erro interno do servidor'
      }

      enqueueSnackbar(errorMessage, {
        variant: 'error',
        autoHideDuration: 4000,
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
      })
    },
  })
}

export default { usePendingValidation, useValidateTicket, useFiltersData }
